import algoliasearch from "algoliasearch/lite"
import differenceBy from "lodash/differenceBy"
import React, { createContext, useCallback, useContext, useEffect, useState } from "react"

import useWindowSize from "../../hooks/useWindowSize"
import { CoordinatesType, MapPinsType, PractitionerSearchType, TherapyCategoryType } from "../../types/SearchPageTypes"

import {
  PerformSearchParams,
  WritableSearchOptions,
  buildSearchString,
  doubleBoundingBoxSize,
  isVirtualView,
  parseMapCenterFromSearchParams,
  performSearch,
  shouldShowMap,
  updateUrl
} from "./SearchUtils"

const searchClient = algoliasearch(
  process?.env?.ALGOLIA_HEALME_SEARCH_APPLICATION_ID ?? "Invalid application ID",
  process?.env?.ALGOLIA_HEALME_SEARCH_CLIENT_API_KEY ?? "Invalid API key"
)

const indexName = "Practice_production"
const countsIndex = searchClient.initIndex(indexName)
const searchIndex = searchClient.initIndex(indexName)

interface FallbackState {
  hits: PractitionerSearchType[]
  nbHits: number
  fetching?: boolean
  queryCompleted?: boolean
}

const FALLBACK_STATE_DEFAULT_VALUE: FallbackState = {
  hits: [],
  nbHits: 0,
  fetching: false,
  queryCompleted: false
}

interface SearchPageContextProps {
  radiusInMiles: number | null
  setRadiusInMiles: React.Dispatch<React.SetStateAction<number | null>>
  isMobile?: boolean
  isTablet?: boolean
  therapyCategories?: TherapyCategoryType[]
  healthIssues?: string[]
  defaultLatLng: CoordinatesType
  languages?: string[]
  ages?: string[]
  mobile?: boolean
  tablet?: boolean
  query: string
  setQuery: React.Dispatch<React.SetStateAction<string>>
  selectedTherapies: string[]
  setSelectedTherapies: React.Dispatch<React.SetStateAction<string[]>>
  selectedHealthIssues: string[]
  setSelectedHealthIssues: React.Dispatch<React.SetStateAction<string[]>>
  selectedLocations: string[]
  setSelectedLocations: React.Dispatch<React.SetStateAction<string[]>>
  selectedLanguages: string[]
  setSelectedLanguages: React.Dispatch<React.SetStateAction<string[]>>
  selectedAges: string[]
  setSelectedAges: React.Dispatch<React.SetStateAction<string[]>>
  locationOptions: string[]
  showMap: boolean
  virtualView: boolean
  mapCenter: CoordinatesType | null
  setMapCenter: React.Dispatch<React.SetStateAction<CoordinatesType | null>>
  mapZoom: number
  setMapZoom: React.Dispatch<React.SetStateAction<number>>
  boundingBox: number[] | undefined
  setBoundingBox: React.Dispatch<React.SetStateAction<number[] | undefined>>
  inPersonCounts: Record<string, number> | null
  healthIssueCounts: Record<string, number> | null
  languageCounts: Record<string, number> | null
  ageCounts: Record<string, number> | null
  hits: PractitionerSearchType[]
  nbHits: number
  offersFreeConsult: boolean
  setOffersFreeConsult: React.Dispatch<React.SetStateAction<boolean>>
  isLastPage: boolean
  loading: boolean
  showMore: () => void
  resetFilters: () => void
  googlePlacesLoaded: boolean
  similarQueryFallbackState: FallbackState
  setSimilarQueryFallbackState: React.Dispatch<React.SetStateAction<FallbackState>>
  nearbyFallbackState: FallbackState
  setNearbyFallbackState: React.Dispatch<React.SetStateAction<FallbackState>>
  virtualFallbackState: FallbackState
  setVirtualFallbackState: React.Dispatch<React.SetStateAction<FallbackState>>
  similarQueryFallbackSearch: () => void
  nearbyFallbackSearch: () => void
  virtualFallbackSearch: () => void
  performedFallbackSearches: boolean
  setPerformedFallbackSearches: React.Dispatch<React.SetStateAction<boolean>>
  mapPins: MapPinsType[]
}

const SEARCH_PAGE_CONTEXT_DEFAULT_VALUE: SearchPageContextProps = {
  radiusInMiles: 0,
  setRadiusInMiles: () => void 0,
  isMobile: false,
  isTablet: false,
  therapyCategories: [],
  healthIssues: [],
  defaultLatLng: { lat: 0, lng: 0 },
  languages: [],
  ages: [],
  mobile: false,
  tablet: false,
  query: "",
  setQuery: () => void 0,
  selectedTherapies: [],
  setSelectedTherapies: () => void 0,
  selectedHealthIssues: [],
  setSelectedHealthIssues: () => void 0,
  selectedLocations: [],
  setSelectedLocations: () => void 0,
  selectedLanguages: [],
  setSelectedLanguages: () => void 0,
  selectedAges: [],
  setSelectedAges: () => void 0,
  locationOptions: [],
  showMap: false,
  virtualView: false,
  mapCenter: null,
  setMapCenter: () => void 0,
  mapZoom: 0,
  setMapZoom: () => void 0,
  boundingBox: undefined,
  setBoundingBox: () => void 0,
  inPersonCounts: null,
  healthIssueCounts: null,
  languageCounts: null,
  ageCounts: null,
  hits: [],
  nbHits: 0,
  offersFreeConsult: false,
  setOffersFreeConsult: () => void 0,
  isLastPage: false,
  loading: false,
  showMore: () => void 0,
  resetFilters: () => void 0,
  googlePlacesLoaded: false,
  similarQueryFallbackState: FALLBACK_STATE_DEFAULT_VALUE,
  setSimilarQueryFallbackState: () => void 0,
  nearbyFallbackState: FALLBACK_STATE_DEFAULT_VALUE,
  setNearbyFallbackState: () => void 0,
  virtualFallbackState: FALLBACK_STATE_DEFAULT_VALUE,
  setVirtualFallbackState: () => void 0,
  similarQueryFallbackSearch: () => void 0,
  nearbyFallbackSearch: () => void 0,
  virtualFallbackSearch: () => void 0,
  performedFallbackSearches: false,
  setPerformedFallbackSearches: () => void 0,
  mapPins: []
}

const SearchPageContext = createContext<SearchPageContextProps>(SEARCH_PAGE_CONTEXT_DEFAULT_VALUE)

interface SearchPageProviderProps
  extends Pick<
    SearchPageContextProps,
    "therapyCategories" | "healthIssues" | "defaultLatLng" | "languages" | "ages" | "mobile" | "tablet"
  > {
  children: React.ReactNode
}

export const SearchPageProvider: React.FC<SearchPageProviderProps> = ({
  children,
  therapyCategories,
  healthIssues,
  defaultLatLng,
  languages,
  ages,
  mobile,
  tablet
}) => {
  const { width } = useWindowSize()
  const [isMobile, setIsMobile] = useState(mobile)
  const [isTablet, setIsTablet] = useState(tablet)

  useEffect(() => {
    if (width) {
      setIsMobile(width ? width < 768 : isMobile)
      setIsTablet(width ? width < 1150 : isTablet)
    }
  }, [width])

  const [urlParams] = useState(
    typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams()
  )
  const defaultQuery = urlParams.get("query") || ""
  const defaultRadius = isTablet ? parseInt(urlParams.get("radius") ?? "25") : null

  // URL mapCenter, then legacy aroundLatLng[lat/lng] from homepage/header, then storage
  let defaultMapCenter = parseMapCenterFromSearchParams(urlParams)
  if (!defaultMapCenter && typeof window !== "undefined") {
    // Check sessionStorage first (where homepage stores coordinates)
    let sessionLatLng: string | null = null
    try {
      sessionLatLng = sessionStorage.getItem("latlng")
    } catch {
      // Storage may throw SecurityError when disabled (iframe sandbox, strict mobile browsers)
    }
    if (sessionLatLng) {
      try {
        const parsed = JSON.parse(sessionLatLng)
        if (Array.isArray(parsed) && parsed.length === 2) {
          const lat = parseFloat(parsed[0])
          const lng = parseFloat(parsed[1])
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            defaultMapCenter = { lat, lng }
          }
        }
      } catch (e) {
        // If parsing fails, continue to localStorage check
      }
    }
    // Fall back to localStorage if sessionStorage didn't have it
    if (!defaultMapCenter) {
      let localMapCenter: string | null = null
      try {
        localMapCenter = localStorage.getItem("mapCenter")
      } catch {
        // Same SecurityError cases as sessionStorage
      }
      if (localMapCenter) {
        try {
          defaultMapCenter = JSON.parse(localMapCenter)
        } catch (e) {
          // If parsing fails, leave as null
        }
      }
    }
  }

  const defaultTherapies = urlParams.get("therapies")?.split(",") || []
  const defaultHealthIssues = urlParams.get("healthIssues")?.split(",") || []

  // Handle locations parameter, with backward compatibility for virtual=true
  let defaultLocations = urlParams.get("locations")?.split(",") || ["office", "home", "virtual"]
  // If virtual=true is in URL and locations is default, set to virtual only
  if (urlParams.get("virtual") === "true" && defaultLocations.length === 3 && defaultLocations.includes("virtual")) {
    defaultLocations = ["virtual"]
  }
  const defaultOffersFreeConsult = urlParams.get("freeConsult") === "true"
  const defaultLanguages = urlParams.get("languages")?.split(",") || []
  const defaultAges = urlParams.get("ages")?.split(",") || []
  // const defaultMapZoom = urlParams.get("mapZoom") ? parseFloat(urlParams.get("mapZoom")) : 12
  const defaultMapZoom = parseFloat(urlParams.get("mapZoom") ?? "12")
  const defaultBoundingBox = urlParams
    .get("boundingBox")
    ?.split(",")
    .map((coord) => parseFloat(coord))

  // search state
  const [query, setQuery] = useState<string>(defaultQuery)
  const [radiusInMiles, setRadiusInMiles] = useState<number | null>(defaultRadius)
  const [mapCenter, setMapCenter] = useState<CoordinatesType | null>(defaultMapCenter)
  const [boundingBox, setBoundingBox] = useState<number[] | undefined>(defaultBoundingBox)
  const [selectedTherapies, setSelectedTherapies] = useState<string[]>(defaultTherapies)
  const [selectedHealthIssues, setSelectedHealthIssues] = useState<string[]>(defaultHealthIssues)
  const [selectedLocations, setSelectedLocations] = useState<string[]>(defaultLocations)
  const [offersFreeConsult, setOffersFreeConsult] = useState<boolean>(defaultOffersFreeConsult)
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(defaultLanguages)
  const [selectedAges, setSelectedAges] = useState<string[]>(defaultAges)
  const [mapZoom, setMapZoom] = useState<number>(defaultMapZoom)

  const [inPersonCounts, setInPersonCounts] = useState<Record<string, number>>({})
  const [healthIssueCounts, setHealthIssueCounts] = useState<Record<string, number> | null>(null)
  const [languageCounts, setLanguageCounts] = useState<Record<string, number> | null>(null)
  const [ageCounts, setAgeCounts] = useState<Record<string, number> | null>(null)
  const [hits, setHits] = useState<PractitionerSearchType[]>([])
  const [nbHits, setNbHits] = useState<number>(0)
  const [isLastPage, setIsLastPage] = useState<boolean>(true)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [isFetchingNextPage, setIsFetchingNextPage] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [poppingState, setPoppingState] = useState<boolean>(false)
  const [previousSearch, setPreviousSearch] = useState<string>("")
  const [googlePlacesLoaded, setGooglePlacesLoaded] = useState<boolean>(false)
  const [shouldDynamicZoom, setShouldDynamicZoom] = useState<boolean>(true)
  const [performedFallbackSearches, setPerformedFallbackSearches] = useState<boolean>(false)
  const [mapPins, setMapPins] = useState<MapPinsType[]>([])

  const showMap = shouldShowMap({ isTablet, mapCenter, selectedLocations })
  const virtualView = isVirtualView({ selectedLocations })
  const locationOptions = ["office", "home", "virtual"]

  const [similarQueryFallbackState, setSimilarQueryFallbackState] =
    useState<FallbackState>(FALLBACK_STATE_DEFAULT_VALUE)
  const [nearbyFallbackState, setNearbyFallbackState] = useState<FallbackState>(FALLBACK_STATE_DEFAULT_VALUE)
  const [virtualFallbackState, setVirtualFallbackState] = useState<FallbackState>(FALLBACK_STATE_DEFAULT_VALUE)

  /*
    Is this useEffect redundant? 
    All logic seems to be handled in MapsWithPins component

    Update: logic to set pins is necessary
  */

  useEffect(() => {
    if (!isMobile && !isTablet) {
      fetch("/practitioner-search/map_pins")
        .then((response) => {
          response.json().then((data) => {
            setMapPins(data)
          })
        })
        .catch((error) => {
          console.error("Error fetching map pins", error) // eslint-disable-line no-console
        })
    }
  }, [])

  // LocationSearchBox dispatches newMapCenter; MapWithPins only mounts on desktop.
  // Listen here so mobile/tablet radius search also updates mapCenter.
  useEffect(() => {
    const onNewMapCenter = (e: Event) => {
      const detail = (e as CustomEvent<{ lat?: number; lng?: number }>).detail
      const lat = detail?.lat
      const lng = detail?.lng
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return

      const nextCenter = { lat: lat as number, lng: lng as number }
      setMapCenter(nextCenter)
      try {
        localStorage.setItem("mapCenter", JSON.stringify(nextCenter))
      } catch {
        // Ignore when localStorage is unavailable (SecurityError)
      }
    }

    document.addEventListener("newMapCenter", onNewMapCenter)
    return () => {
      document.removeEventListener("newMapCenter", onNewMapCenter)
    }
  }, [])

  useEffect(() => {
    const searchParams = {
      searchIndex,
      selectedTherapies,
      selectedHealthIssues,
      selectedLocations,
      offersFreeConsult,
      selectedLanguages,
      selectedAges,
      currentPage,
      boundingBox,
      showMap,
      radiusInMiles,
      mapCenter,
      isTablet,
      query
    }

    performSearch(searchParams, (searchResponse) => {
      const { hits, nbHits, facets, nbPages, page } = searchResponse

      const searchString = buildSearchString(searchParams)

      let newSearch = false
      if (searchString !== previousSearch) {
        newSearch = true
        setCurrentPage(0)
        setPreviousSearch(searchString)
        setHits([])
        setLoading(true)
        setSimilarQueryFallbackState(FALLBACK_STATE_DEFAULT_VALUE)
        setNearbyFallbackState(FALLBACK_STATE_DEFAULT_VALUE)
        setVirtualFallbackState(FALLBACK_STATE_DEFAULT_VALUE)
      }

      if (facets?.health_issues) setHealthIssueCounts(facets.health_issues)
      if (facets?.languages) setLanguageCounts(facets.languages)
      if (facets?.age_work_withs) setAgeCounts(facets.age_work_withs)

      setNbHits(nbHits)

      if (newSearch || page === 0) {
        setHits(hits)
      } else {
        setHits((prevHits) => [...prevHits, ...hits])
      }

      setSimilarQueryFallbackState(FALLBACK_STATE_DEFAULT_VALUE)
      setNearbyFallbackState(FALLBACK_STATE_DEFAULT_VALUE)
      setVirtualFallbackState(FALLBACK_STATE_DEFAULT_VALUE)
      setPerformedFallbackSearches(false)
      setIsLastPage(page + 1 >= nbPages)
      setIsFetchingNextPage(false)
      setLoading(false)

      if (searchParams.showMap && shouldDynamicZoom) {
        let newZoom
        if (nbHits <= 5 && mapZoom > 9) {
          newZoom = mapZoom - 1
        } else if (nbHits > 50 && mapZoom < 13) {
          newZoom = mapZoom + 1
        }
        if (newZoom !== undefined) {
          setMapZoom(newZoom)
          const event = new CustomEvent("newMapZoom", { detail: newZoom })
          document.dispatchEvent(event)
        }
      }

      setShouldDynamicZoom(false)
      if (page === 0) window.scrollTo(0, 0)

      updateUrl({
        query,
        radiusInMiles,
        mapCenter,
        boundingBox,
        mapZoom,
        selectedTherapies,
        selectedHealthIssues,
        selectedLocations,
        offersFreeConsult,
        selectedLanguages,
        selectedAges,
        isMobile,
        poppingState,
        setPoppingState
      })
    })
  }, [
    query,
    boundingBox,
    radiusInMiles,
    selectedTherapies,
    selectedHealthIssues,
    selectedLocations,
    selectedLanguages,
    selectedAges,
    mapCenter,
    showMap,
    offersFreeConsult,
    currentPage
  ])

  useEffect(() => {
    const countsSearchParams: WritableSearchOptions = {
      filters: "location_types:office OR location_types:home",
      hitsPerPage: 100
    }

    if (boundingBox) {
      countsSearchParams.insideBoundingBox = [boundingBox]
    } else if (mapCenter && radiusInMiles) {
      countsSearchParams.aroundLatLng = `${mapCenter.lat},${mapCenter.lng}`
      countsSearchParams.aroundRadius = radiusInMiles * 1609
    } else {
      return
    }

    countsIndex.search<PractitionerSearchType>("", countsSearchParams).then(({ hits }) => {
      const tempInPersonCounts: Record<string, number> = {}

      hits
        .flatMap((hit) => hit.therapies)
        .forEach((therapy) => {
          tempInPersonCounts[therapy] = tempInPersonCounts[therapy] ? tempInPersonCounts[therapy]! + 1 : 1
        })

      setInPersonCounts(tempInPersonCounts)
    })
  }, [boundingBox])

  useEffect(() => {
    if (loading === false) {
      const timeout = setTimeout(() => {
        const savedScrollPosition = sessionStorage.getItem("scrollPosition") || "0"
        sessionStorage.removeItem("scrollPosition")
        window.scrollTo(0, parseInt(savedScrollPosition, 10))
      }, 300)

      return () => clearTimeout(timeout)
    }
  }, [loading])

  const showMore = () => {
    if (isFetchingNextPage) return
    setCurrentPage((prev) => prev + 1)
    setIsFetchingNextPage(true)
  }

  const resetFilters = useCallback(() => {
    setQuery("")
    setSelectedTherapies([])
    setSelectedHealthIssues([])
    setSelectedLocations(["office", "home", "virtual"])
    setOffersFreeConsult(false)
    setSelectedLanguages([])
    setSelectedAges([])
    setSimilarQueryFallbackState({ hits: [], nbHits: 0 })
    setNearbyFallbackState({ hits: [], nbHits: 0 })
    setVirtualFallbackState({ hits: [], nbHits: 0 })
    document.dispatchEvent(new CustomEvent("resetSearchField"))
    window.scrollTo(0, 0)
  }, [])

  const searchParams = {
    searchIndex,
    selectedTherapies,
    selectedHealthIssues,
    selectedLocations,
    offersFreeConsult,
    selectedLanguages,
    selectedAges,
    currentPage,
    boundingBox,
    showMap,
    radiusInMiles,
    mapCenter,
    isTablet,
    query
  }

  const similarQueryFallbackSearch = () => {
    const newSearchParams: PerformSearchParams = {
      ...searchParams,
      similarQuery: true,
      hitsPerPage: 25,
      currentPage: 0
    }
    setSimilarQueryFallbackState((state) => ({ ...state, fetching: true }))
    performSearch(newSearchParams, (response) => {
      if (response.nbHits > 0) {
        const newHits = differenceBy(response.hits, hits, "objectID")
        setSimilarQueryFallbackState({ hits: newHits, nbHits: newHits.length, fetching: false, queryCompleted: true })
      } else {
        setSimilarQueryFallbackState({ hits: [], nbHits: 0, fetching: false, queryCompleted: true })
      }
    })
  }

  const nearbyFallbackSearch = () => {
    const newSearchParams: PerformSearchParams = {
      ...searchParams,
      boundingBox: doubleBoundingBoxSize(searchParams.mapCenter, searchParams.boundingBox) ?? [],
      radiusInMiles: (searchParams.radiusInMiles ?? 0) * 2,
      hitsPerPage: 25,
      currentPage: 0
    }
    setNearbyFallbackState((state) => ({ ...state, fetching: true }))
    performSearch(newSearchParams, (response) => {
      if (response.nbHits > 0) {
        const newHits = differenceBy(response.hits, hits, "objectID")
        setNearbyFallbackState({ hits: newHits, nbHits: newHits.length, fetching: false, queryCompleted: true })
      } else {
        setNearbyFallbackState({ hits: [], nbHits: 0, fetching: false, queryCompleted: true })
      }
    })
  }

  const virtualFallbackSearch = () => {
    const additionalFilters = therapyCategories
      ?.filter((therapy) => therapy.inPersonOnly)
      .map((therapy) => `NOT therapies:"${therapy.name}"`)
      .join(" AND ")
    const virtualSearchParams: PerformSearchParams = {
      ...searchParams,
      selectedLocations: ["virtual"],
      additionalFilters,
      virtualFallback: true,
      hitsPerPage: 25,
      currentPage: 0
    }

    setVirtualFallbackState((state) => ({ ...state, fetching: true }))
    performSearch(virtualSearchParams, (response) => {
      if (response.nbHits > 0) {
        const newHits = differenceBy(response.hits, hits, "objectID")
        setVirtualFallbackState({ hits: newHits, nbHits: newHits.length, fetching: false, queryCompleted: true })
      } else {
        setVirtualFallbackState({ hits: [], nbHits: 0, fetching: false, queryCompleted: true })
      }
    })
  }

  return (
    <SearchPageContext.Provider
      value={{
        radiusInMiles,
        setRadiusInMiles,
        isMobile,
        isTablet,
        therapyCategories,
        healthIssues,
        languages,
        ages,
        query,
        setQuery,
        selectedTherapies,
        setSelectedTherapies,
        selectedHealthIssues,
        setSelectedHealthIssues,
        selectedLocations,
        setSelectedLocations,
        selectedLanguages,
        setSelectedLanguages,
        selectedAges,
        setSelectedAges,
        locationOptions,
        showMap,
        virtualView,
        mapCenter,
        setMapCenter,
        mapZoom,
        setMapZoom,
        boundingBox,
        setBoundingBox,
        inPersonCounts,
        healthIssueCounts,
        languageCounts,
        ageCounts,
        hits,
        nbHits,
        defaultLatLng,
        offersFreeConsult,
        setOffersFreeConsult,
        isLastPage,
        loading,
        showMore,
        resetFilters,
        googlePlacesLoaded,
        similarQueryFallbackState,
        setSimilarQueryFallbackState,
        nearbyFallbackState,
        setNearbyFallbackState,
        virtualFallbackState,
        setVirtualFallbackState,
        similarQueryFallbackSearch,
        nearbyFallbackSearch,
        virtualFallbackSearch,
        performedFallbackSearches,
        setPerformedFallbackSearches,
        mapPins
      }}>
      {children}
    </SearchPageContext.Provider>
  )
}

export const useSearchPageContext = () => useContext(SearchPageContext)
